/**
 * The editor's Totals card (design C). It only lays out figures it is
 * given: the live preview while typing, the server's document after a save.
 * Line and invoice discounts are split by summing the lines' own discounts.
 */
import React from 'react';
import { money } from '../format';
import type { PreviewDoc } from './InvoicePreview';

interface Row { l: string; v: string; strong?: boolean }

export default function TotalsPanel({
  doc, invoiceDiscountLabel,
}: {
  doc: PreviewDoc;
  /** e.g. " (5%)" when the invoice discount is a percentage. */
  invoiceDiscountLabel?: string;
}) {
  const cur = doc.currency || 'LKR';
  const isQuote = doc.docType === 'QUOTATION';
  const lineDisc = (doc.lines || []).reduce((a, l) => a + (l.discountMinor || 0), 0);
  const invDisc = Math.max(0, doc.discountMinor - lineDisc);
  const taxed = doc.taxLines.length > 0;
  const vatWord = doc.isTaxInvoice ? 'VAT' : 'tax';

  const rows: Row[] = [{ l: 'Subtotal', v: money(doc.subtotalMinor, cur) }];
  if (lineDisc) rows.push({ l: 'Line discounts', v: `− ${money(lineDisc, cur)}` });
  if (invDisc) rows.push({ l: `Invoice discount${invoiceDiscountLabel || ''}`, v: `− ${money(invDisc, cur)}` });
  (doc.charges || []).forEach((c) => rows.push({ l: c.label || 'Charge', v: money(c.amountMinor, cur) }));
  if (taxed) {
    rows.push({ l: `Value before ${vatWord}`, v: money(doc.taxLines.reduce((a, t) => a + t.baseMinor, 0), cur) });
    doc.taxLines.forEach((t) => rows.push({ l: t.label || `Tax ${t.rateBp / 100}%`, v: money(t.taxMinor, cur) }));
  }
  if (doc.roundingMinor) rows.push({ l: 'Rounding', v: money(doc.roundingMinor, cur) });

  const after: Row[] = [];
  if (!isQuote) {
    if (doc.depositAppliedMinor) after.push({ l: 'Deposit received', v: `− ${money(doc.depositAppliedMinor, cur)}` });
    if (doc.paidMinor) after.push({ l: 'Paid', v: `− ${money(doc.paidMinor, cur)}` });
    if (doc.creditedMinor) after.push({ l: 'Credit notes', v: `− ${money(doc.creditedMinor, cur)}` });
  }

  return (
    <section aria-label="Totals" className="flex flex-col gap-1.5 rounded-2xl border border-borderc bg-surface px-5 py-[18px] tabular-nums">
      <h2 className="mb-1.5 text-base font-semibold text-text1">Totals</h2>
      {rows.map((r, i) => (
        <div key={`${r.l}-${i}`} className="flex justify-between gap-3 text-sm text-text2">
          <span>{r.l}</span>
          <span className="nl-num">{r.v}</span>
        </div>
      ))}
      <div className="mt-0.5 flex justify-between gap-3 border-t border-borderc pt-2 text-[15px] font-bold text-text1">
        <span>{doc.isTaxInvoice ? 'Total including VAT' : 'Total'}</span>
        <span className="nl-num">{money(doc.totalMinor, cur)}</span>
      </div>
      {after.map((r) => (
        <div key={r.l} className="flex justify-between gap-3 text-sm text-text2">
          <span>{r.l}</span>
          <span className="nl-num">{r.v}</span>
        </div>
      ))}
      <div className="mt-1.5 flex justify-between gap-3 rounded-[10px] bg-brand-soft px-3.5 py-3 text-base font-bold text-brand">
        <span>{isQuote ? 'Quotation total' : 'Balance due'}</span>
        <span className="nl-num">{money(isQuote ? doc.totalMinor : doc.balanceMinor, cur)}</span>
      </div>
      {doc.isTaxInvoice && doc.amountInWords && (
        <span className="text-xs italic text-text2">{doc.amountInWords}</span>
      )}
    </section>
  );
}
